import { Download, FileDownload, Share } from '@mui/icons-material';
import { CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import SharePopup from '/@/components/share-popup';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { emailRecommendationPDF, getRecommendationPDF } from '/@/store/private-data/recommendation-pdfs/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Recommendation } from '/@/types/recommendation';
import ModalV2 from '../bayer/modal-v2';
import type { ShareItemPopupMode } from '../share-item-popup';

interface RecommendationShareProps {
    recommendation: Recommendation;
    classNameRoot?: string;
    disabled?: boolean;
    type?: string;
    version?: number;
    /** PDF template to request. Omitted by default, so the backend renders the original. */
    pdfVersion?: number;
    entrySource?: string;
}

export default function RecommendationShare({
    recommendation,
    classNameRoot,
    disabled,
    type,
    version = 1,
    pdfVersion,
    entrySource,
}: RecommendationShareProps) {
    const [showPopup, setShowPopup] = useState(false);
    // Version 2 is the only template with selectable sections, so it's the only one whose
    // buttons open the picker.
    const picksBlocks = pdfVersion === 2;
    const [popupMode, setPopupMode] = useState<ShareItemPopupMode | undefined>(undefined);
    const openPopup = (mode: ShareItemPopupMode) => {
        setPopupMode(mode);
        setShowPopup(true);
    };
    const [isDownloading, setIsDownloading] = useState(false);
    const [fileProps, setFileProps] = useState(undefined);
    const dispatch = useDispatch();

    const handleShowPopup = () => {
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    const handleEmail = async ({ email }: { email: string }) => {
        if (!type && recommendation?.weeds?.length > 0) type = 'old-weed';

        const file = await dispatch(emailRecommendationPDF({ recommendation, email, version: pdfVersion }));

        return {
            file,
            type,
            source: 'recommendation-detail',
            numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
        };
    };

    const handleWhatsApp = async () => {
        if (!type && recommendation?.weeds?.length > 0) type = 'old-weed';

        const file = await dispatch(getRecommendationPDF({ recommendation, version: pdfVersion }));

        return {
            file,
            type,
            source: 'recommendation-detail',
            numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
        };
    };

    const handleDownload = async () => {
        if (!type && recommendation?.weeds?.length > 0) type = 'old-weed';

        try {
            setIsDownloading(true);
            const file = await dispatch(getRecommendationPDF({ recommendation, download: true, version: pdfVersion }));
            setIsDownloading(false);
            setFileProps({
                file,
                type,
                source: 'recommendation-detail',
                numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
            });

            return {
                file,
                type,
                source: 'recommendation-detail',
                numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
            };
        } catch (error) {
            setIsDownloading(false);
            setFileProps(undefined);
        }
    };

    return (
        <>
            {version === 1 ? (
                <div className={classNameRoot}>
                    <Button variant='outlined' fullWidth onClick={handleShowPopup} disabled={disabled} color='primary'>
                        <Tooltip
                            title={
                                disabled
                                    ? _('Para poder compartir una recomendación, debes guardarla primero')
                                    : undefined
                            }
                            arrow
                        >
                            <Typography sx={{ lineHeight: 'inherit', fontWeight: 'inherit' }}>
                                {_('Compartir')}
                            </Typography>
                        </Tooltip>
                    </Button>
                </div>
            ) : (
                <Stack direction='row' spacing={1}>
                    <Button
                        variant='outlined'
                        onClick={picksBlocks ? () => openPopup('generate') : handleDownload}
                        disabled={disabled}
                        color='primary'
                        endIcon={<FileDownload />}
                        sx={{ backgroundColor: '#fff' }}
                        data-testid='recommendation-share-download-button'
                    >
                        {picksBlocks ? _('Generar PDF') : _('Descargar')}
                    </Button>
                    <Button
                        variant='contained'
                        onClick={picksBlocks ? () => openPopup('share') : handleShowPopup}
                        disabled={disabled}
                        color='secondary'
                        endIcon={<Share />}
                        data-testid='recommendation-share-share-button'
                    >
                        <Tooltip
                            title={
                                disabled
                                    ? _('Para poder compartir una recomendación, debes guardarla primero')
                                    : undefined
                            }
                            arrow
                        >
                            {picksBlocks ? _('Compartir') : _('Compartir recomendación')}
                        </Tooltip>
                    </Button>
                </Stack>
            )}
            <SharePopup
                visible={showPopup}
                title={_('Compartir recomendación')}
                whatsAppIntro={_('Te envío una recomendación generada en {{ appName }}', {
                    appName: (settings as any).app?.name || 'Cultivio',
                })}
                onClose={handleClosePopup}
                onEmail={handleEmail}
                onWhatsApp={handleWhatsApp}
                type={type}
                version={version}
                recommendation={recommendation}
                entrySource={entrySource}
                pdfVersion={pdfVersion}
                mode={popupMode}
            />
            <ModalV2
                open={isDownloading || fileProps}
                onClose={() => setFileProps(undefined)}
                title={_('Descargar PDF')}
                data-testid='recommendation-share-download-modal'
                actions={
                    <Button
                        startIcon={fileProps ? <Download /> : <CircularProgress size={20} />}
                        loading={isDownloading}
                        disabled={!fileProps}
                        data-testid={`recommendation-share-download-modal-button-${fileProps ? 'download' : 'generating'}`}
                        onClick={async () => {
                            if (fileProps) {
                                // Don't await to update optimistically, email is sent via offline sync mechanism.
                                const { crop, source, numberOfHybrids } = fileProps;
                                if (type === 'crop-protection') {
                                    mpTrackEvent('Crop Protection Recommendation downloaded', {
                                        entry_source: 'my-recommendations',
                                        crop: recommendation.type,
                                        cp_type: recommendation.typeLabel,
                                    });
                                } else if (['corn', 'soy', 'weed'].includes(type)) {
                                    mpTrackEvent('Seed recommendation downloaded', {
                                        crop,
                                        number_of_hybrids: numberOfHybrids,
                                        entry_source: source,
                                    });
                                } else if (type === 'density') {
                                    mpTrackEvent('Corn Density Recommendation downloaded', undefined);
                                }
                                window.open(fileProps.file.url);
                            }
                        }}
                    >
                        {fileProps ? _('Descargar PDF') : _('Generando PDF...')}
                    </Button>
                }
                actionsProps={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <Typography variant='subtitle1' color='primary'>
                    {!fileProps
                        ? _('Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.')
                        : _('¡Tu recomendación está lista!')}
                </Typography>
            </ModalV2>
        </>
    );
}
